import axios, { AxiosError } from "axios";
import { getServerAuthToken } from "@shared/src/utils/server-cookies";
import { cookieManager } from "@shared/src/lib/auth/cookieManager";
import { useHostStore } from "../stores/useHostStore";

axios.defaults.adapter = "fetch";

// Base configuration
const baseConfig = {
  baseURL: process.env.API_BASE_URL,
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
  },
  adapter: "fetch",
};

// Custom error class for API errors
export class APIError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = "APIError";
  }
}

// Error handler function
const handleAxiosError = (error: AxiosError) => {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data as any;
    const backendMessage = data?.message;

    // Common error status codes with fallback messages
    const errorMessages = {
      401: "Your session has expired. Please sign in again.",
      403: "You are not authorized to perform this action.",
      404: "The requested resource was not found.",
      422: "Invalid data provided. Please check your input.",
      429: "Too many requests. Please try again later.",
      500: "An internal server error occurred. Please try again later.",
      502: "Service temporarily unavailable. Please try again later.",
      503: "Service temporarily unavailable. Please try again later.",
      504: "Service temporarily unavailable. Please try again later.",
    };

    // Special case for "Forbidden resource" message
    const message =
      backendMessage === "Forbidden resource"
        ? errorMessages[403]
        : backendMessage ||
          errorMessages[status as keyof typeof errorMessages] ||
          "An unexpected error occurred. Please try again.";
    const code = data?.code || status.toString();

    throw new APIError(message, status, code);
  }

  if (error.request) {
    throw new APIError("No response received from server. Please check your connection.", undefined, "NETWORK_ERROR");
  }

  throw new APIError("Failed to make request. Please try again.", undefined, "REQUEST_ERROR");
};

// Public instance (no auth required)
export const publicAxios = axios.create(baseConfig);

// General instance (for non-API requests)
export const generalAxios = axios.create({ adapter: "fetch" });

// Protected instance (auth required)
export const protectedAxios = axios.create(baseConfig);

// Add request interceptor for authentication on protected routes
protectedAxios.interceptors.request.use(async config => {
  let token: string | null = null;

  if (typeof window !== "undefined") {
    // Client-side: use our cookie hook
    const { getAuthToken } = cookieManager();
    token = getAuthToken(); 
  } else {
    // Server-side: get from server cookie
    token = await getServerAuthToken();
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Add response interceptors for both instances
[publicAxios, protectedAxios, generalAxios].forEach(instance => {
  instance.interceptors.response.use(
    response => response,
    error => {
      return Promise.reject(handleAxiosError(error));
    },
  );
});

// Default export for backward compatibility
export default protectedAxios;
